import { inject, Injectable } from '@angular/core';
import {
    HttpRequest,
    HttpHandler,
    HttpEvent,
    HttpInterceptor,
    HttpErrorResponse,
    HttpClient,
    HttpHeaders,
    HttpResponse,
    HttpParams,
    HttpEventType,
    HttpStatusCode,
    HttpHandlerFn
} from '@angular/common/http';
import { from, Observable, of, throwError } from 'rxjs';

import { catchError, finalize, switchMap, map, tap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { AuthorizationService } from '../services/authorization.service';
import { ToastModule } from 'primeng/toast';
import { MessageService, ToastMessageOptions } from 'primeng/api';
import { GraphQLResponse } from '@shared/zigra-schemas/zigra-schema.autogenerated.models';
import { ZionMessagesService } from '@shared/services/ax-messages.service';
import * as Sentry from '@sentry/angular';

/* import { DataService } from 'src/services/data.service'; */
/* import { HttpClientHelperService } from 'src/helpers/http-client.helper.service'; */

// export enum APIVersionsEnum {
//   Version_1_0_LatestDev = '1.0-dev',
//   Version_1_0_Alpha = '1.0-Alpha',
// }

export function httpErrorInterceptor(
    req: HttpRequest<unknown>,
    next: HttpHandlerFn
): Observable<HttpEvent<unknown>> {
    const authorizationService = inject(AuthorizationService);
    const router = inject(Router);
    const messageService = inject(ZionMessagesService);

    return next(req).pipe(
        tap((httpEvent) => {
            // console.debug(httpEvent.type, httpEvent);
            switch (httpEvent.type) {
                case HttpEventType.Sent:
                    break;
                case HttpEventType.DownloadProgress:
                    break;
                case HttpEventType.Response:
                    if (httpEvent.status == HttpStatusCode.Ok) {
                        const body = httpEvent.body as GraphQLResponse<any>;
                        // console.trace(
                        //     `[${new Date().toISOString()}] HttpEvent Status: Ok. Http Response body: `,
                        //     httpEvent.body
                        // );

                        if ((body?.errors ?? []).length > 0) {
                            Sentry.captureException(
                                new Error(JSON.stringify(body)),
                                {
                                    tags: {
                                        source: 'error-interceptor',
                                        graphQL: true
                                    }
                                }
                            );
                            console.error('GraphQL Error Response: ', body);
                            messageService.add({
                                summary: 'Σφάλμα Επικοινωνίας',
                                detail: body?.errors?.[0]?.Message,
                                severity: 'error',
                                key: 'br'
                            } as ToastMessageOptions);

                            throw body?.errors?.[0]?.Message;
                        }
                    }
                    break;
                case HttpEventType.ResponseHeader:
                    break;
                case HttpEventType.UploadProgress:
                    break;
                case HttpEventType.User:
                    break;
            }
        }),
        catchError((err) => {
            if (
                req.url.toLowerCase().search('connect.routee.net/2step') !==
                    -1 ||
                req.url.toLowerCase().search('connect/token') !== -1
            ) {
                Sentry.captureException(err, {
                    tags: { source: 'error-interceptor' }
                });

                authorizationService.HttpErrorToastMessage?.(
                    {
                        severity: 'error',
                        summary: 'Σφάλμα Επικοινωνίας',
                        detail: err.error?.message || err.statusText
                    },
                    'error-interceptor'
                );
                return throwError(() => err);
            }

            if ([401, 403].includes(err.status)) {
                // auto logout if 401 or 403 response returned from api
                if (err.headers.has('Www-Authenticate')) {
                    const wwwAuthenticateHader =
                        err.headers.get('Www-Authenticate');
                    if (
                        wwwAuthenticateHader
                            ?.toLowerCase()
                            .indexOf(
                                'Bearer error="invalid_token"'.toLowerCase()
                            ) > -1
                    ) {
                        console.debug(
                            `[${new Date().toISOString()}] Authorization: Invalid Token. Refresh Token.`
                        );

                        // Sentry.captureMessage(
                        //     'Authorization: Invalid Token. Refresh Token.',
                        //     'warning'
                        // ); // or 'warning', etc.

                        const refreshToken = from(
                            authorizationService.reauthorizeUser(req, next)
                        ).pipe(
                            switchMap((request) => {
                                return next(request);
                            }),
                            catchError((error) => {
                                Sentry.captureException(error, {
                                    tags: { source: 'error-interceptor' }
                                });

                                authorizationService.HttpErrorToastMessage?.(
                                    {
                                        severity: 'error',
                                        summary: 'Σφάλμα Επικοινωνίας',
                                        detail:
                                            err.error?.message || err.statusText
                                    },
                                    'error-interceptor'
                                );
                                return throwError(() => error);
                            })
                        );

                        return refreshToken;
                    }
                }

                return authorizationService.revokeUser().pipe(
                    map((value) => {
                        router.navigate(['/auth/login']).then();
                        return value;
                    })
                );
            }

            const error = err.error?.message || err.statusText;

            authorizationService.HttpErrorToastMessage?.(
                {
                    severity: 'error',
                    summary: 'Σφάλμα Επικοινωνίας',
                    detail: err.error?.message || err.statusText
                },
                'error-interceptor'
            );

            //const sentryErr = new Error('Non-throwing error path');
            Sentry.captureException(err, {
                tags: { source: 'error-interceptor' }
            });

            return throwError(() => err);
        })
    );
}

// @Injectable()
// export class HttpErrorInterceptor implements HttpInterceptor {
//   constructor(private authorizationService: AuthorizationService) {}

//   intercept(
//     request: HttpRequest<unknown>,
//     next: HttpHandlerFn
//   ) {
//     // if (this.activeRequests === 0) {
//     // this.loaderService.loadLoader.next(true);
//     // } connect.routee.net
//     // this.activeRequests++;

//     return next(request).pipe(
//       catchError((err) => {
//         if ([401, 403].includes(err.status)) {
//           // auto logout if 401 or 403 response returned from api
//           this.authorizationService.revokeUser().subscribe();
//         }

//         const error = err.error?.message || err.statusText;
//         console.error(err);
//         return throwError(() => error);
//       }),
//       tap((httpEvent) => {
//         console.trace(httpEvent.type, httpEvent);
//         switch (httpEvent.type) {
//           case HttpEventType.Sent:
//             break;
//           case HttpEventType.DownloadProgress:
//             break;
//           case HttpEventType.Response:
//             if (httpEvent.status == HttpStatusCode.Ok) {
//               const body = httpEvent.body;
//               console.trace(
//                 `[${new Date().toISOString()}] HttpEvent Status: Ok. Http Response body: `,
//                 httpEvent.body
//               );
//             }
//             break;
//           case HttpEventType.ResponseHeader:
//             break;
//           case HttpEventType.UploadProgress:
//             break;
//           case HttpEventType.User:
//             break;
//         }
//       })
//     );

//     // return next.handle(request).pipe(
//     //   // finalize(() => {}),
//     //   // The map operator will simply apply a function to that data and return the result.
//     //   // tslint:disable-next-line:max-line-length
//     //   // The tap operator however takes a data, apply a function to that data but returns the original data, if the function bothered to return a result, tap just ignores it.
//     //   tap({
//     //     next: (httpEvent: HttpEvent<any>) => {
//     //       console.log(httpEvent.type, httpEvent);
//     //       switch (httpEvent.type) {
//     //         case HttpEventType.Sent:
//     //           break;
//     //         case HttpEventType.DownloadProgress:
//     //           break;
//     //         case HttpEventType.Response:
//     //           if (httpEvent.status == HttpStatusCode.Ok) {
//     //             const body = httpEvent.body;
//     //             console.trace(
//     //               `[${new Date().toISOString()}] HttpEvent Status: Ok. Http Response body: `,
//     //               httpEvent.body
//     //             );
//     //           }
//     //           break;
//     //         case HttpEventType.ResponseHeader:
//     //           break;
//     //         case HttpEventType.UploadProgress:
//     //           break;
//     //         case HttpEventType.User:
//     //           break;
//     //       }
//     //     },
//     //   }),
//     //   tap((httpEvent: HttpEvent<any>) => {
//     //     //     debugger;
//     //     //     // console.info('Request Interceptor:', request);
//     //     //     if (rawResult.body) {
//     //     //       const result = rawResult.body;
//     //     //       if (result.errors?.length > 0)
//     //     //       {
//     //     //         if (result.errors.find((e: { extensions: { code: string; }; }) => e.extensions?.code === 'UNAUTHORIZED_USER_ACCESS'))
//     //     //         {
//     //     //           throw new Error('UNAUTHORIZED_USER_ACCESS');
//     //     //           // return throwError(401);
//     //     //         }
//     //     //       }
//     //     //       // return rawResult.body;
//     //     //     }
//     //     // debugger;
//     //     // // Skip request
//     //     // // if(httpEvent.type === 0){
//     //     // //     return;
//     //     // // }
//     //     // console.log("response: ", httpEvent);
//     //     // let minTargetApiVersion : string;
//     //     // if (httpEvent instanceof HttpResponse) {
//     //     //     if(httpEvent.headers.has('www-authenticate')) {
//     //     //       debugger;
//     //     //       const tminTargetApiVersion = httpEvent.headers.get('Www-Authenticate');
//     //     //     }
//     //     // }
//     //   }),
//     //   // tap({
//     //   //   complete: () => {},
//     //   //   error: () => {},
//     //   //   next: (rawResult: any) => {
//     //   //     debugger;
//     //   //     // console.info('Request Interceptor:', request);
//     //   //     if (rawResult.body) {
//     //   //       const result = rawResult.body;
//     //   //       if (result.errors?.length > 0)
//     //   //       {
//     //   //         if (result.errors.find((e: { extensions: { code: string; }; }) => e.extensions?.code === 'UNAUTHORIZED_USER_ACCESS'))
//     //   //         {
//     //   //           throw new Error('UNAUTHORIZED_USER_ACCESS');
//     //   //           // return throwError(401);
//     //   //         }
//     //   //       }
//     //   //       // return rawResult.body;
//     //   //     }
//     //   //   }
//     //   // }),
//     //   catchError((httpError: HttpErrorResponse) => {
//     //     //error.message === 'UNAUTHORIZED_USER_ACCESS' ||
//     //     if (
//     //       httpError instanceof HttpErrorResponse &&
//     //       httpError.status === 401
//     //     ) {
//     //       // auto logout if 401 response returned from api
//     //       // this.authenticationService.logout();
//     //       // location.reload(true);

//     //       //const httpError = (error as HttpErrorResponse);
//     //       if (httpError && httpError.headers.has('www-authenticate')) {
//     //         const wwwAuthenticateHader =
//     //           httpError.headers.get('www-authenticate'); // v
//     //         if (
//     //           wwwAuthenticateHader &&
//     //           wwwAuthenticateHader.indexOf('Bearer error="invalid_token"') > -1
//     //         ) {
//     //           console.info(
//     //             `[${new Date().toISOString()}] Authorization: Invalid Token. Refresh Token.`
//     //           );
//     //           return this.authorizationService.reauthorizeUser(request, next);
//     //         }
//     //       }
//     //     }

//     //     if (
//     //       httpError instanceof HttpErrorResponse &&
//     //       httpError.status === 400
//     //     ) {
//     //       // 400 bad Reequest
//     //       if (request.url.indexOf('/token') > -1) {
//     //         const openIdDictError = httpError.error as OpenIdDictError;
//     //         console.info(
//     //           `[${new Date().toISOString()}] Authorization: ${
//     //             openIdDictError?.error_description
//     //           }.`,
//     //           openIdDictError
//     //         );
//     //         this.authorizationService.revokeUser().subscribe();
//     //         /*
//     //           Possibly invalid Grant, do not refresh device token eg:

//     //           {
//     //             "error": "invalid_grant",
//     //             "error_description": "The specified refresh token is invalid.",
//     //             "error_uri": "https://documentation.openiddict.com/errors/ID2003"
//     //           }
//     //         */
//     //       }
//     //     }

//     //     return throwError(() => httpError);

//     //     // // Εδώ θα έρθει και το 401 Error
//     //     // if (httpError.message === 'UNAUTHORIZED_USER_ACCESS' || httpError instanceof HttpErrorResponse && httpError.status === 401) {
//     //     //   return this.handle401Error(httpError, request, next);
//     //     // }
//     //   })
//     // );
//   }
// }

export interface OpenIdDictError {
    error: string; //"invalid_grant",
    error_description: string; // "The specified refresh token is no longer valid.",
    error_uri: string; //"https://documentation.openiddict.com/errors/ID2018"
}
